/**
 * FundraisersTable.tsx - Dashboard Fundraisers Management Table
 *
 * This component displays fundraisers in a comprehensive table format
 * with sorting, filtering, and management actions for dashboard use.
 *
 * Key Features:
 * - Comprehensive fundraiser data display
 * - Sortable columns (name, amount, status, date)
 * - Status indicators and progress bars
 * - Inline action buttons and dropdown menus
 * - Responsive table design with mobile adaptations
 * - Empty state handling for filtered results
 *
 * Table Columns:
 * - Fundraiser name and image
 * - Current amount and goal with progress
 * - Status with visual indicators
 * - Creation date and last activity
 * - Actions dropdown for management
 *
 * Props:
 * - fundraisers: IAPIFundraiser[] - Array of fundraisers to display
 * - searchTerm: string - Current search filter
 * - hasActiveFilters: boolean - Whether filters are applied
 * - onRowClick: (fundraiser: IAPIFundraiser) => void - Row click handler
 * - onViewDetails: (fundraiser: IAPIFundraiser) => void - View details handler
 * - onEdit: (fundraiser: IAPIFundraiser) => void - Edit handler
 * - onDonate: (fundraiser: IAPIFundraiser) => void - Donate handler
 * - onInvite: (fundraiser: IAPIFundraiser) => void - Invite handler
 * - onLink: (fundraiser: IAPIFundraiser) => void - Link management handler
 * - onShare: (fundraiser: IAPIFundraiser) => void - Share handler
 * - onEmbed: (fundraiser: IAPIFundraiser) => void - Embed handler
 * - onCopy: (fundraiser: IAPIFundraiser) => void - Copy handler
 * - onDeleteClick: (fundraiser: IAPIFundraiser) => void - Delete handler
 *
 * Dependencies: IAPIFundraiser types, portal rendering, action components
 */

import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Search } from "lucide-react";
import { FundraiserActionsDropdown } from "./FundraiserActionsDropdown";
import {
  getExpirationBadgeText,
  shouldShowExpirationBadge,
  getPendingBadgeText,
  shouldShowPendingBadge,
} from "../../utils/badgesUtils";
import { CreateFundraiserModel } from "../../models";
import { FundRaiserStatus, IconType } from "../../enums";
import { Fundraiser } from "../../models/FundraisersGridModels";
import * as Icons from "lucide-react";
import { LogoIcon, LogoImage } from "../Header";

const StyledFundraisersTable = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  min-height: 500px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2.5fr 1.2fr 1.2fr 1fr 120px;
  padding: 20px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2.5fr 1.2fr 1.2fr 1fr 120px;
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const FundraiserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  > div {
    min-height: 48px;
    min-width: 48px;
  }
`;

const FundraiserThumb = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
`;

const FundraiserDetails = styled.div`
  text-align: left;
`;

const FundraiserTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const FundraiserMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
`;

const StatusBadge = styled.div<{ status: keyof typeof FundRaiserStatus }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.status) {
      case "Active":
        return "background: #dcfce7; color: #166534;";
      case "Completed":
        return "background: #dbeafe; color: #1e40af;";
      case "Inactive":
        return "background: #fef3c7; color: #92400e;";
      case "Draft":
        return "background: #f3f4f6; color: #374151;";
      default:
        return "background: #f3f4f6; color: #374151;";
    }
  }}
`;

const ExpirationBadge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  white-space: nowrap;
`;

const PendingBadge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #93c5fd;
  white-space: nowrap;
`;

const ProgressColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressAmounts = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const ProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SmallProgressBar = styled.div`
  width: 43.2%;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
`;

const SmallProgressFill = styled.div<{ percentage: number }>`
  width: ${(props) => Math.min(props.percentage, 100)}%;
  height: 100%;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 3px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  animation: progressLoadTable 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes progressLoadTable {
    from {
      width: 0%;
    }
    to {
      width: ${(props) => Math.min(props.percentage, 100)}%;
    }
  }
`;

const ProgressPercentage = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #000000;
  min-width: 45px;
`;

const DonationsColumn = styled.div`
  text-align: left;
  padding-left: 0;
`;

const LastDonation = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #64748b;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: #94a3b8;
`;

const EmptyStateTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

const Tooltip = styled.div<{ visible: boolean; x: number; y: number }>`
  position: fixed;
  top: ${(props) => props.y}px;
  left: ${(props) => props.x}px;
  background: rgba(0, 0, 0, 0.9);
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 9999;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.2s ease;
  pointer-events: none;
  transform: translateX(-50%) translateY(-100%);
  margin-top: -8px;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

interface FundraisersTableProps {
  fundraisers: CreateFundraiserModel[];
  searchTerm: string;
  hasActiveFilters: boolean;
  onRowClick: (fundraiserId: string) => void;
  onViewDetails: (fundraiserId: string) => void;
  onEdit: (fundraiserId: string) => void;
  onDonate: (ffundraiserId: string) => void;
  onInvite: (fundraiserId: string) => void;
  onLink: (fundraiserId: string) => void;
  onShare: (fundraiserId: string) => void;
  onEmbed: (fundraiserId: string) => void;
  onCopy: (fundraiser: CreateFundraiserModel) => void;
  onDeleteClick: (fundraiser: CreateFundraiserModel) => void;
}

export const FundraisersTable: React.FC<FundraisersTableProps> = ({
  fundraisers,
  searchTerm,
  hasActiveFilters,
  onRowClick,
  onViewDetails,
  onEdit,
  onDonate,
  onInvite,
  onLink,
  onShare,
  onEmbed,
  onCopy,
  onDeleteClick,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatLastDonationDate = (date: Date) => {
    const currentYear = new Date().getFullYear();
    const donationYear = date.getFullYear();

    if (donationYear === currentYear) {
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
      }).format(date);
    } else {
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date);
    }
  };

  const calculateActualProgress = (currentAmount: number, goal: number) => {
    return goal > 0 ? (currentAmount / goal) * 100 : 0;
  };

  const renderIcon = (iconName: string) => {
    if (!iconName || !(iconName in Icons)) return null;
    const Icon = (Icons as any)[iconName];
    return <Icon size={28} />;
  };

  return (
    <div>
      <StyledFundraisersTable>
        <TableHeader>
          <div>Name</div>
          <div>Progress</div>
          <div>Donations</div>
          <div>Promoters</div>
          <div>Actions</div>
        </TableHeader>

        {fundraisers.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <Search size={40} />
            </EmptyStateIcon>
            <EmptyStateTitle>No Fundraisers Found</EmptyStateTitle>
            <EmptyStateText>
              {searchTerm || hasActiveFilters
                ? "Try adjusting your search terms or filters to find what you're looking for."
                : "Create your first fundraiser to get started!"}
            </EmptyStateText>
          </EmptyState>
        ) : (
          fundraisers.map((fundraiser, index) => {
            const actualProgress = calculateActualProgress(
              fundraiser.totalRaisedAmount,
              fundraiser.goal
            );
            const displayProgress = actualProgress;
            const barProgress = Math.min(actualProgress, 100);

            // Get the primary image from mediaItems or fallback to image property

            // Badge priority logic: Pending takes priority over Expiration
            const shouldShowPendingBadgeForFundraiser = shouldShowPendingBadge(
              fundraiser.startDate || "",
              fundraiser.status
            );
            const shouldShowExpirationBadgeForFundraiser =
              !shouldShowPendingBadgeForFundraiser &&
              shouldShowExpirationBadge(
                fundraiser.endDate || "",
                fundraiser.status
              );

            return (
              <TableRow
                key={fundraiser.id}
                onClick={() => onRowClick(fundraiser.id)}
              >
                <FundraiserInfo>
                  <div>
                    {fundraiser.getPrimaryImageToShow() ? (
                      <FundraiserThumb
                        src={fundraiser.getPrimaryImageToShow()}
                        alt={fundraiser.name}
                      // onError={(e) => {
                      //   const target = e.target as HTMLImageElement;
                      //   target.src =
                      //     "https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?w=400&h=300&fit=crop";
                      // }}
                      />
                    ) : (
                      fundraiser.iconImageChoice === IconType.Icon &&
                      fundraiser.titleColor && (
                        <LogoIcon backgroundColor={"#FFFFFF"} isLogo={true}>
                          <div
                            style={{
                              color: fundraiser.titleColor.hex
                                ? fundraiser.titleColor.hex
                                : fundraiser.titleColor.hex,
                            }}
                          >
                            {fundraiser.titleIcon &&
                              renderIcon(fundraiser.titleIcon)}
                          </div>
                        </LogoIcon>
                      )
                    )}
                  </div>
                  <FundraiserDetails>
                    <FundraiserTitle>{fundraiser.name}</FundraiserTitle>
                    <FundraiserMeta>
                      <StatusBadge status={fundraiser.status}>
                        {fundraiser.status}
                      </StatusBadge>
                      {shouldShowExpirationBadgeForFundraiser &&
                        fundraiser.endDate && (
                          <ExpirationBadge>
                            {getExpirationBadgeText(
                              new Date(fundraiser.endDate)
                            )}
                          </ExpirationBadge>
                        )}
                      {shouldShowPendingBadgeForFundraiser &&
                        fundraiser.startDate && (
                          <PendingBadge>
                            {getPendingBadgeText(
                              new Date(fundraiser.startDate)
                            )}
                          </PendingBadge>
                        )}
                    </FundraiserMeta>
                  </FundraiserDetails>
                </FundraiserInfo>

                <ProgressColumn>
                  <ProgressAmounts>
                    {formatCurrency(fundraiser.totalRaisedAmount)} /{" "}
                    {formatCurrency(fundraiser.goal)}
                  </ProgressAmounts>
                  <ProgressBarContainer>
                    <SmallProgressBar>
                      <SmallProgressFill percentage={barProgress} />
                    </SmallProgressBar>
                    <ProgressPercentage>
                      {displayProgress.toFixed(1)}%
                    </ProgressPercentage>
                  </ProgressBarContainer>
                </ProgressColumn>

                <DonationsColumn>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1e293b",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: "28px",
                      height: "48px",
                      minHeight: "48px",
                    }}
                  >
                    <span>{fundraiser.totalDonations}</span>
                    {fundraiser.lastDonatedOn && (
                      <span style={{ color: "#64748b", fontWeight: "500" }}>
                        Last {formatLastDonationDate(fundraiser.lastDonatedOn)}
                      </span>
                    )}
                  </div>
                </DonationsColumn>

                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1e293b",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      height: "48px",
                      minHeight: "48px",
                    }}
                  >
                    {fundraiser.activePromoters || 0}
                  </div>
                </div>

                <div onClick={(e) => e.stopPropagation()}>
                  <FundraiserActionsDropdown
                    isLastThreeRows={index >= fundraisers.length - 3}
                    onEdit={() => onEdit(fundraiser.id)}
                    onDetails={() => onViewDetails(fundraiser.id)}
                    onDonate={() => onDonate(fundraiser.id)}
                    onInvite={() => onInvite(fundraiser.id)}
                    onLink={() => onLink(fundraiser.id)}
                    onShare={() => onShare(fundraiser.id)}
                    onEmbed={() => onEmbed(fundraiser.id)}
                    onCopy={() => onCopy(fundraiser)}
                    onDelete={() => onDeleteClick(fundraiser)}
                  />
                </div>
              </TableRow>
            );
          })
        )}
      </StyledFundraisersTable>
    </div>
  );
};
